import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { resetShieldsPassword } from "@/lib/native-auth";

export const Route = createFileRoute("/reset-password")({
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    ...(typeof search["token"] === "string" && search["token"].length <= 512
      ? { token: search["token"] }
      : {}),
  }),
  head: () => ({
    meta: [
      { title: "Set a new password — Shields" },
      { name: "description", content: "Choose a new password for your Shields account." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { token: nativeToken } = Route.useSearch();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [valid, setValid] = useState(Boolean(nativeToken));
  const navigate = useNavigate();

  useEffect(() => {
    if (nativeToken) {
      setValid(true);
      return;
    }

    // Legacy recovery links remain readable during the controlled Step 83
    // transition. New Shields-native migration links use ?token= and never put
    // the reset secret in localStorage.
    const hash = new URLSearchParams(window.location.hash.slice(1));
    if (hash.get("type") === "recovery") {
      setValid(true);
      return;
    }
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") setValid(true);
    });
    return () => data.subscription.unsubscribe();
  }, [nativeToken]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);

    try {
      if (nativeToken) {
        await resetShieldsPassword(nativeToken, password);
        toast.success("Password updated — sign in with your new password");
        await navigate({ to: "/auth", replace: true });
        return;
      }

      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      toast.success("Password updated");
      await navigate({ to: "/account" });
    } catch (resetError) {
      setError(resetError instanceof Error ? resetError.message : "Unable to update your password");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="card-surface w-full max-w-md p-8">
        <div className="flex justify-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <ShieldCheck className="h-5 w-5" aria-hidden />
          </span>
        </div>
        <h1 className="mt-4 text-center text-xl font-bold">Set a new password</h1>
        {!valid ? (
          <p className="mt-4 text-center text-sm text-muted-foreground">
            This reset link is invalid or has expired.{" "}
            <Link to="/auth" className="font-semibold text-primary underline">
              Request a new one
            </Link>
            .
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            {error && (
              <p role="alert" className="rounded-lg bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">
                {error}
              </p>
            )}
            <div>
              <Label htmlFor="password">New password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                minLength={12}
                required
                className="mt-1.5 min-h-12"
              />
              <p className="mt-1.5 text-xs text-muted-foreground">Use at least 12 characters.</p>
            </div>
            <Button type="submit" disabled={busy} className="min-h-12 w-full font-bold">
              {busy ? "Updating…" : "Update password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
